import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, tap, throwError } from 'rxjs';

import { isTokenRequest } from '../../shared/utils/is-token-request.util';
import { redactSensitive, redactUrl } from '../../shared/utils/redact-sensitive.util';
import { ClusterService } from '../services/context/cluster.service';
import { ToolbeltContextService } from '../services/context/toolbelt-context.service';
import { type RequestLogEntry, RequestLogService } from '../services/firestore/request-log.service';
import { SKIP_ACTIVITY_LOG } from './skip-activity-log.token';

/**
 * Functional HTTP interceptor that persists every mutating request (non-GET) to
 * RequestLogService, so we can later see which clients trigger the most config changes and
 * eventually train a prediction model on it. Modeled on notifications.interceptor.ts, which
 * already proves a global interceptor sees every call regardless of which service issued it
 * (ToolbeltHttpService, NfDatasourceService, CoulsonApiService, FuryApiService all share the
 * one HttpClient instance).
 *
 * Every non-GET method is logged (POST/PUT/PATCH/DELETE) — GETs are excluded because they'd
 * dominate write volume/cost without answering "which configs did we make". Anything that
 * looks like it's just fetching a token (e.g. the Nick Fury backend's firebase-token call)
 * is excluded too — that's internal auth plumbing, not a config change, and logging it
 * would just dilute the "which client did what" signal. Same exclusion is applied in
 * notifications.interceptor.ts so that traffic doesn't show up in the notifications drawer
 * either.
 *
 * The service is resolved from the target host (Coulson, Fury, Sentinel, or other),
 * making the audit trail useful even when a feature calls a service beyond Coulson/Fury.
 */
export function requestLogInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const cluster = inject(ClusterService);
  const backend = resolveBackend(req.urlWithParams, cluster);
  if (req.method === 'GET' || isTokenRequest(req.urlWithParams) || req.context.get(SKIP_ACTIVITY_LOG)) {
    return next(req);
  }

  const requestLog = inject(RequestLogService);
  const context = inject(ToolbeltContextService);
  const router = inject(Router);

  const startedAt = performance.now();
  const { toolId, categoryCode } = resolveRouteParams(router);
  const ctx = context.resolveAll();

  const buildEntry = (statusCode: number, ok: boolean, responseBody: unknown): RequestLogEntry => ({
    method: req.method,
    url: redactUrl(req.urlWithParams),
    backend,
    statusCode,
    ok,
    responseTimeMs: Math.round(performance.now() - startedAt),
    clientId: extractClientId(req),
    storeId: ctx['storeId'] ?? '',
    userId: ctx['userId'] ?? '',
    env: ctx['env'] ?? '',
    toolId,
    categoryCode,
    requestPayload: redactSensitive(req.body),
    responseBody: redactSensitive(responseBody),
  });

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        requestLog.log(buildEntry(event.status, event.ok, event.body));
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        requestLog.log(buildEntry(error.status, false, error.error));
      }
      return throwError(() => error);
    }),
  );
}

function resolveBackend(url: string, cluster: ClusterService): RequestLogEntry['backend'] {
  const coulsonHost = hostOf(cluster.coulsonBaseUrl());
  const furyHost = hostOf(cluster.furyBaseUrl() ?? undefined);
  const sentinelHost = hostOf(cluster.sentinelBaseUrl() ?? undefined);
  const requestHost = hostOf(url);

  if (requestHost && requestHost === coulsonHost) return 'coulson';
  if (requestHost && requestHost === furyHost) return 'fury';
  if (requestHost && requestHost === sentinelHost) return 'sentinel';
  return 'other';
}

/**
 * Client attribution must be evidence-based: it is shown only when the outgoing request itself
 * carries a client value — in the body, a header, or the URL (path segment or query param).
 * Context selections are deliberately not used because they can be unrelated to the action that
 * was sent. Checked in this order since the body is the richest/most specific source; the first
 * match wins.
 */
function extractClientId(req: HttpRequest<unknown>): string {
  return (
    extractClientFromBody(req.body) ||
    extractClientFromHeaders(req.headers) ||
    extractClientFromUrl(req.urlWithParams)
  );
}

/** Normalizes a key/segment for comparison so 'clientId', 'client_id', 'X-Client-Id', etc. all match the same candidate. */
function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const CLIENT_KEYS = new Set(['clientid', 'client', 'clientcode', 'xclientid', 'xclient', 'xclientcode']);
/** Path segments after which the *next* segment is treated as the client id, e.g. `/clients/{id}` or `/logistic-clients/{id}`. */
const CLIENT_PATH_SEGMENTS = new Set(['clients', 'client', 'logisticclients', 'logisticclient']);

function extractClientFromBody(body: unknown): string {
  if (!body || typeof body !== 'object') return '';

  const stack: unknown[] = [body];

  while (stack.length) {
    const value = stack.pop();
    if (!value || typeof value !== 'object') continue;
    if (Array.isArray(value)) {
      stack.push(...value);
      continue;
    }

    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      if (CLIENT_KEYS.has(normalizeKey(key)) && typeof item === 'string' && item.trim()) {
        return item.trim();
      }
      if (item && typeof item === 'object') stack.push(item);
    }
  }

  return '';
}

function extractClientFromHeaders(headers: HttpHeaders): string {
  for (const key of headers.keys()) {
    if (!CLIENT_KEYS.has(normalizeKey(key))) continue;
    const value = headers.get(key)?.trim();
    if (value) return value;
  }
  return '';
}

function extractClientFromUrl(urlWithParams: string): string {
  let parsed: URL;
  try {
    parsed = new URL(urlWithParams, window.location.origin);
  } catch {
    return '';
  }

  for (const [key, value] of parsed.searchParams.entries()) {
    if (CLIENT_KEYS.has(normalizeKey(key)) && value.trim()) return value.trim();
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  for (let i = 0; i < segments.length - 1; i++) {
    if (CLIENT_PATH_SEGMENTS.has(normalizeKey(segments[i]))) {
      const candidate = decodeURIComponent(segments[i + 1]).trim();
      if (candidate) return candidate;
    }
  }

  return '';
}

function hostOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).hostname;
  } catch {
    return null;
  }
}

/** Walks the activated route tree to find the deepest `:categoryCode`/`:toolId` params. */
function resolveRouteParams(router: Router): { toolId: string; categoryCode: string } {
  let route = router.routerState.snapshot.root;
  let toolId = '';
  let categoryCode = '';

  while (route) {
    toolId = route.params['toolId'] ?? toolId;
    categoryCode = route.params['categoryCode'] ?? categoryCode;
    if (!route.firstChild) break;
    route = route.firstChild;
  }

  return { toolId, categoryCode };
}
